import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { TrashIcon, PlusCircleIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, getFirestore, serverTimestamp, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import JobSearch from '../../JobSearch';
import JobActivityFeed from '../../features/dashboard/jobs/JobActivityFeed';
import FastTrackActions from '../../features/dashboard/jobs/FastTrackActions';
import { addRecommendedJobToUser, removeRecommendedJobFromUser, archiveJobForUser } from '../../../services/firebase';
import JobFormModal from '../JobFormModal';

export default function CandidateJobsTab({ user }) {
  const [newJobId, setNewJobId] = useState('');
  const [isAddingJob, setIsAddingJob] = useState(false);
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [recommendedJobDetails, setRecommendedJobDetails] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [userAddedJobs, setUserAddedJobs] = useState([]);
  const [userLikedJobs, setUserLikedJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [jobReactions, setJobReactions] = useState({});
  const [recommendJob, setRecommendJob] = useState({});
  // New state variables for JobFormModal
  const [isJobFormModalOpen, setIsJobFormModalOpen] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);

  // Fetch user jobs and recommended jobs when component mounts
  useEffect(() => {
    if (user && user.id) {
      fetchUserJobs();
      fetchUserRecommendedJobs();
    }
  }, [user]);

  // Fetch recommended jobs details
  useEffect(() => {
    if (recommendedJobs && recommendedJobs.length > 0) {
      fetchRecommendedJobsDetails();
    }
  }, [recommendedJobs]);

  // Fetch liked jobs details
  useEffect(() => {
    if (userLikedJobs && userLikedJobs.length > 0) {
      fetchLikedJobsDetails();
    }
  }, [userLikedJobs]);

  const fetchUserJobs = async () => {
    try {
      setIsLoadingJobs(true);
      const db = getFirestore();
      const userProfileRef = doc(db, "user_profiles", user.id);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        const userData = userProfileSnap.data();
        // Get user's own added jobs
        const addedJobs = userData.addedJobs || [];
        
        if (addedJobs.length > 0) {
          const jobDetails = await fetchJobDetailsByIds(addedJobs);
          setUserAddedJobs(jobDetails);
        }
      }
    } catch (error) {
      console.error('Error fetching user jobs:', error);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const fetchJobDetailsByIds = async (jobIds) => {
    if (!jobIds.length) return [];
    
    try {
      const db = getFirestore();
      const jobsRef = collection(db, "jobs");
      const jobsData = [];
      
      // Process in batches to avoid potential limitations
      for (const jobId of jobIds) {
        const jobQuery = query(jobsRef, where("id", "==", jobId));
        const querySnapshot = await getDocs(jobQuery);
        
        querySnapshot.forEach((doc) => {
          jobsData.push({
            id: doc.id,
            ...doc.data()
          });
        });
      }
      
      return jobsData;
    } catch (error) {
      console.error("Error fetching job details:", error);
      return [];
    }
  };

  // Enhance fetchJobDetails to get recommendation details
  const fetchJobDetails = async (jobId) => {
    try {
      const db = getFirestore();
      const jobRef = doc(db, "jobs", jobId);
      const jobSnap = await getDoc(jobRef);
      
      if (jobSnap.exists()) {
        const jobData = jobSnap.data();
        const userReactions = jobData.userReactions || {};
        
        // Check if this user has reacted to this job
        const userReaction = userReactions[user.id] || null;
        
        // Get user profile for recommendJob data
        const userProfileRef = doc(db, "user_profiles", user.id);
        const userProfileSnap = await getDoc(userProfileRef);
        let recommendData = null;
        
        if (userProfileSnap.exists()) {
          const userData = userProfileSnap.data();
          // Get recommendation data from the recommendJob map
          recommendData = userData.recommendJob && userData.recommendJob[jobId] 
            ? userData.recommendJob[jobId] 
            : null;
        }
        
        return {
          id: jobSnap.id,
          ...jobSnap.data(),
          userReaction, // Include the user reaction data
          recommendData // Include the recommendation data
        };
      } else {
        console.log(`Job with ID ${jobId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      return null;
    }
  };

  const fetchUserRecommendedJobs = async () => {
    try {
      const db = getFirestore();
      const userProfileRef = doc(db, "user_profiles", user.id);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        const userData = userProfileSnap.data();
        // Get recommended jobs from the recommendJob map
        const recommendJobMap = userData.recommendJob || {};
        setRecommendJob(recommendJobMap);
        // Extract job IDs from the recommendJob map
        const recommendedJobIds = Object.keys(recommendJobMap);
        setRecommendedJobs(recommendedJobIds);
        // Set liked jobs from profile data
        setUserLikedJobs(userData.likedJobs || []);
        // Set job reactions if available
        setJobReactions(userData.jobReactions || {});
      }
    } catch (error) {
      console.error('Error fetching user recommended jobs:', error);
    }
  };

  const fetchRecommendedJobsDetails = async () => {
    try {
      const jobDetails = [];
      for (const jobId of recommendedJobs) {
        const jobDetail = await fetchJobDetails(jobId);
        if (jobDetail) {
          jobDetails.push(jobDetail);
        }
      }
      setRecommendedJobDetails(jobDetails);
    } catch (error) {
      console.error("Error fetching recommended job details:", error);
    }
  };

  const fetchLikedJobsDetails = async () => {
    try {
      const jobDetails = await fetchJobDetailsByIds(userLikedJobs);
      setUserLikedJobs(jobDetails);
    } catch (error) {
      console.error("Error fetching liked job details:", error);
    }
  };

  const handleJobSelect = (job) => {
    if (job && job.id) {
      handleAddJobRecommendation({ preventDefault: () => {} }, job.id);
    }
  };

  // Handler for creating a new job and recommending it to the user
  const handleCreateAndRecommendJob = async (jobData) => {
    try {
      setIsCreatingJob(true);
      const db = getFirestore();
      const jobsRef = collection(db, "jobs");
      
      // Add the job to Firestore
      const newJobRef = await addDoc(jobsRef, {
        ...jobData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Get the job ID
      const newJobId = newJobRef.id;
      
      // Update the job with its ID (for easier querying)
      await updateDoc(newJobRef, {
        id: newJobId
      });
      
      toast.success('Job created successfully!');
      
      // Recommend the job to the user
      await handleAddJobRecommendation({ preventDefault: () => {} }, newJobId);
      
      // Close the modal
      setIsJobFormModalOpen(false);
      
      // Refresh the jobs list
      await fetchUserRecommendedJobs();
      
    } catch (error) {
      console.error('Error creating and recommending job:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsCreatingJob(false);
    }
  };

  const handleAddJobRecommendation = async (e, jobId = null) => {
    e.preventDefault();
    const idToAdd = jobId || newJobId.trim();
    
    if (!idToAdd) return;
    
    setIsAddingJob(true);
    try {
      const auth = getAuth();
      const adminUid = auth.currentUser?.uid;
      
      if (!adminUid) {
        toast.error('You must be logged in to add a job recommendation');
        return;
      }
      
      const result = await addRecommendedJobToUser(idToAdd, user.id, adminUid);
      
      if (result.success) {
        toast.success('Job recommendation added');
        setNewJobId('');
        // Refresh the recommended jobs list
        await fetchUserRecommendedJobs();
      } else {
        toast.error(`Failed to add job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error adding job recommendation:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsAddingJob(false);
    }
  };

  const handleRemoveJobRecommendation = async (jobId) => {
    try {
      const result = await removeRecommendedJobFromUser(jobId, user.id);
      
      if (result.success) {
        toast.success('Job recommendation removed');
        // Update the local state
        setRecommendedJobs(recommendedJobs.filter(id => id !== jobId));
        setRecommendedJobDetails(recommendedJobDetails.filter(job => job.id !== jobId));
      } else {
        toast.error(`Failed to remove job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error removing job recommendation:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleArchiveJobForUser = async (jobId) => {
    try {
      const result = await archiveJobForUser(jobId, user.id);
      
      if (result.success) {
        toast.success('Job archived for user');
        // Update the local state
        setRecommendedJobs(recommendedJobs.filter(id => id !== jobId));
        setRecommendedJobDetails(recommendedJobDetails.filter(job => job.id !== jobId));
      } else {
        toast.error(`Failed to archive job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error archiving job for user:', error);
      toast.error(`Error: ${error.message}`);
    }
  };
  
  const getJobActivities = (job) => {
    const activities = [];
    
    if (job.recommendData) {
      activities.push({
        type: 'recommended',
        timestamp: job.recommendData.timestamp?.toDate() || new Date(),
        by: job.recommendData.adminUid || 'admin'
      });
    }
    
    if (job.userReaction === 'like') {
      activities.push({
        type: 'liked',
        timestamp: jobReactions[job.id]?.timestamp?.toDate() || new Date()
      });
    } else if (job.userReaction === 'dislike') {
      activities.push({
        type: 'disliked',
        timestamp: jobReactions[job.id]?.timestamp?.toDate() || new Date()
      });
    }
    
    return activities;
  };

  // Filter out possible duplicates from the userAddedJobs array
  const filteredUserAddedJobs = userAddedJobs.filter(job => 
    !recommendedJobDetails.some(recJob => recJob.id === job.id)
  );

  if (!user) return null;

  return (
    <div className="space-y-8">
      {/* Job Search */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Add Job Recommendation</h3>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <div className="w-full">
              <JobSearch onJobSelect={handleJobSelect} isLoading={isSearching} />
            </div>
            <div className="flex-none">
              <button
                type="button"
                onClick={() => setIsJobFormModalOpen(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
              >
                <PlusCircleIcon className="h-5 w-5 mr-1" />
                Create New
              </button>
            </div>
          </div>
          <div className="mt-6">
            <form onSubmit={(e) => handleAddJobRecommendation(e)} className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <div className="w-full">
                <label htmlFor="jobId" className="sr-only">
                  Job ID
                </label>
                <input
                  type="text"
                  id="jobId"
                  name="jobId"
                  value={newJobId}
                  onChange={(e) => setNewJobId(e.target.value)}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  placeholder="Enter job ID manually"
                />
              </div>
              <div className="flex-none">
                <button
                  type="submit"
                  disabled={isAddingJob || !newJobId.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50"
                >
                  {isAddingJob ? 'Adding...' : 'Add Job'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Recommended Jobs */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Recommended Jobs</h3>
          
          {isLoadingJobs ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading jobs...</p>
            </div>
          ) : recommendedJobDetails.length > 0 ? (
            <div className="space-y-4">
              {recommendedJobDetails.map((job) => (
                <div key={job.id} className="bg-white dark:bg-gray-700 rounded-md shadow overflow-hidden">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex justify-between">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                        {job.title || 'Untitled Job'}
                      </h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleArchiveJobForUser(job.id)}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900 hover:bg-amber-200 dark:hover:bg-amber-800 focus:outline-none"
                        >
                          <ArchiveBoxIcon className="h-4 w-4 mr-1" />
                          Archive
                        </button>
                        <button
                          onClick={() => handleRemoveJobRecommendation(job.id)}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 focus:outline-none"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Remove
                        </button>
                      </div>
                    </div>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                      {job.company || 'Unknown Company'} • {job.location || 'Remote'}
                    </p>
                    
                    {/* Job Activities */}
                    <div className="mt-4">
                      <JobActivityFeed activities={getJobActivities(job)} />
                    </div>
                    
                    {/* Fast Track Actions */}
                    <div className="mt-4">
                      <FastTrackActions job={job} userId={user.id} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 italic">No recommended jobs found for this user.</p>
          )}
        </div>
      </div>

      {/* Job Form Modal */}
      <JobFormModal
        isOpen={isJobFormModalOpen}
        onClose={() => setIsJobFormModalOpen(false)}
        onSubmit={handleCreateAndRecommendJob}
        isCreating={isCreatingJob}
      />
    </div>
  );
} 